import { leaveManagementServiceApi } from "../../app/leaveManagementServiceApi";
import { API_URL } from "../../constants/apiUrl";
import {
  GetAdjustmentParams,
  GetDownloadEmployeeLeaveBalanceType,
  GetEmployeeProrateListParams,
  GetEmployeeProrateListResponseType,
  GetLeaveAdjustmentLogType,
  GetLeaveBalanceDetailsParams,
  GetLeaveBalanceDetailsResponseType,
  GetLeaveOthersResponseType,
  GetLeaveProrateDetailsType,
  GetLeaveProrateEmployeeParams,
  GetLeaveProrateEmployeeResponseType,
  GetQueryArgsType,
  PatchQueryArgsType,
} from "../../types";

export const leaveProrateApi = leaveManagementServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    addOtherSetting: builder.mutation({
      query: (data) => ({
        url: API_URL.addOtherSetting,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetOtherSettingData"],
    }),
    getOtherSettingData: builder.query<GetLeaveOthersResponseType, void>({
      query: () => ({
        url: API_URL.getOtherSettingData,
      }),
      providesTags: ["GetOtherSettingData"],
    }),
    addLeaveProrateManually: builder.mutation({
      query: (data) => ({
        url: API_URL.addLeaveProrateManually,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetLeaveProrateEmployeeList"],
    }),
    addLeaveAutoProrateRun: builder.mutation({
      query: (data) => ({
        url: API_URL.addLeaveAutoProrateRun,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetLeaveProrateEmployeeList"],
    }),
    addLeaveOldUserAutoProrateRun: builder.mutation({
      query: (data) => ({
        url: API_URL.addLeaveOldUserAutoProrateRun,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetLeaveProrateEmployeeList"],
    }),
    getLeaveProrateEmployeeList: builder.query<
      GetLeaveProrateEmployeeResponseType,
      GetQueryArgsType<GetLeaveProrateEmployeeParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getLeaveProrateEmployeeList,
        ...queryArgs,
      }),
      providesTags: ["GetLeaveProrateEmployeeList"],
    }),
    getEmployeeProrateList: builder.query<
      GetEmployeeProrateListResponseType,
      GetQueryArgsType<GetEmployeeProrateListParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getEmployeeProrateList,
        ...queryArgs,
      }),
      providesTags: ["GetEmployeeProrateList"],
    }),
    getLeaveProrateDetails: builder.query<GetLeaveProrateDetailsType, number>({
      query: (id) => ({
        url: `${API_URL.getLeaveProrateDetails}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetLeaveProrateDetails", id }],
    }),
    getLeaveAdjustmentLog: builder.query<
      GetLeaveAdjustmentLogType,
      GetQueryArgsType<GetAdjustmentParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getLeaveAdjustmentLog,
        ...queryArgs,
      }),
      providesTags: () => ["GetLeaveAdjustmentLog"],
    }),
    updateLeaveProrate: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ id, body }) => ({
        url: `${API_URL.updateLeaveProrate}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetEmployeeProrateList",
            "GetLeaveAdjustmentLog",
            {
              type: "GetLeaveProrateDetails",
              id,
            },
          ];

        return [];
      },
    }),
    deleteLeaveProrate: builder.mutation({
      query: (data) => ({
        url: API_URL.deleteLeaveProrate,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetEmployeeProrateList"],
    }),
    getLeaveBalance: builder.query({
      query: () => ({
        url: API_URL.getLeaveBalance,
      }),
    }),
    getLeaveBalanceDetails: builder.query<
      GetLeaveBalanceDetailsResponseType,
      GetQueryArgsType<GetLeaveBalanceDetailsParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getLeaveBalanceDetails,
        ...queryArgs,
      }),
    }),
    getLeaveType: builder.query({
      query: () => ({
        url: API_URL.getLeaveType,
      }),
    }),
    getLeaveTypeById: builder.query({
      query: (id) => ({
        // url: `${API_URL.getLeaveTypeById}/${id}`,
      }),
    }),
    singleEmployeeProrateRun: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ id, body }) => ({
        url: `${API_URL.singleEmployeeProrateRun}/${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            "GetEmployeeProrateList",
            "GetLeaveAdjustmentLog",
            {
              type: "GetLeaveProrateDetails",
              id,
            },
          ];

        return [];
      },
    }),

    downloadEmployeeLeaveBalance: builder.query<
      void,
      GetQueryArgsType<GetDownloadEmployeeLeaveBalanceType>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.downloadEmployeeLeaveBalance}`,
        responseHandler: async (response: any) => {
          const blob = await response.blob(); // Convert response to blob
          return window.URL.createObjectURL(blob); // Create object URL
        },
        cache: "no-cache",
        ...queryArgs,
      }),
    }),
  }),
});

export const {
  useAddOtherSettingMutation,
  useGetOtherSettingDataQuery,
  useAddLeaveProrateManuallyMutation,
  useAddLeaveAutoProrateRunMutation,
  useGetLeaveProrateDetailsQuery,
  useUpdateLeaveProrateMutation,
  useGetLeaveTypeQuery,
  useGetLeaveTypeByIdQuery,
  useGetLeaveProrateEmployeeListQuery,
  useGetEmployeeProrateListQuery,
  useDeleteLeaveProrateMutation,
  useGetLeaveBalanceQuery,
  useGetLeaveBalanceDetailsQuery,
  useSingleEmployeeProrateRunMutation,
  useLazyDownloadEmployeeLeaveBalanceQuery,
  useAddLeaveOldUserAutoProrateRunMutation,
  useGetLeaveAdjustmentLogQuery,
} = leaveProrateApi;
